import { gql } from "@apollo/client"

import { ApiFormattedResponse } from "@models/api_formatted_response"
import {
  type AdminCampaignLinkToggle,
  type AdminCampaignLinks,
  type CampaignTrackingLink
} from "@models/campaign_links"
import { mutate, query } from "@utils/graphql"

// Hand-written until api-v2's QPIEN-1398 schema is deployed and `yarn refresh:backend:dev` can
// introspect it; the selections mirror api-v2 `Campaign/controller/campaign.types.gql`.
const LINKS_STATUS = `
  disabled
  disabledAt
  reason
  disabledBy
`

const LINK_ROW = `
  _id
  title
  status
  statusReason
  startedAt
  createdAt
  deletedAt
  destinationUrl
  clicked
  links { ${LINKS_STATUS} }
`

const ADMIN_CAMPAIGN_LINKS = `
  customerId
  customerName
  links { ${LINKS_STATUS} }
  campaigns { ${LINK_ROW} }
  pausedCampaigns
`

const getAdminCampaignLinksDoc = gql`
  query getAdminCampaignLinks($customerId: ID!) {
    getAdminCampaignLinks(customerId: $customerId) {
      success
      code
      message
      data { ${ADMIN_CAMPAIGN_LINKS} }
    }
  }
`

const resolveCampaignTrackingLinkDoc = gql`
  query resolveCampaignTrackingLink($url: String!) {
    resolveCampaignTrackingLink(url: $url) {
      success
      code
      message
      data {
        kind
        customerId
        customerName
        customerLinks { ${LINKS_STATUS} }
        campaign { ${LINK_ROW} }
        templateId
        templateName
        destinationUrl
      }
    }
  }
`

const setCampaignLinksDisabledDoc = gql`
  mutation setCampaignLinksDisabled($input: SetCampaignLinksDisabledInput!) {
    setCampaignLinksDisabled(input: $input) {
      success
      code
      message
      data {
        paused
        campaign { ${LINK_ROW} }
      }
    }
  }
`

const setCustomerLinksDisabledDoc = gql`
  mutation setCustomerLinksDisabled($input: SetCustomerLinksDisabledInput!) {
    setCustomerLinksDisabled(input: $input) {
      success
      code
      message
      data { ${ADMIN_CAMPAIGN_LINKS} }
    }
  }
`

export const CAMPAIGN_LINKS_QUERY_KEYS = {
  customer: "admin-campaign-links",
  lookup: "campaign-tracking-link"
} as const

export async function getAdminCampaignLinks(
  customerId: string
): Promise<ApiFormattedResponse<AdminCampaignLinks>> {
  const response = await query<{
    getAdminCampaignLinks?: { data: AdminCampaignLinks }
  }>({
    query: getAdminCampaignLinksDoc,
    fetchPolicy: "network-only",
    variables: { customerId }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return { isSuccess: true, data: response.data?.getAdminCampaignLinks?.data }
}

export async function resolveCampaignTrackingLink(
  url: string
): Promise<ApiFormattedResponse<CampaignTrackingLink>> {
  const response = await query<{
    resolveCampaignTrackingLink?: { data: CampaignTrackingLink }
  }>({
    query: resolveCampaignTrackingLinkDoc,
    fetchPolicy: "network-only",
    variables: { url }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.resolveCampaignTrackingLink?.data
  }
}

export async function setCampaignLinksDisabled(input: {
  campaignId: string
  disabled: boolean
  reason?: string
  pause?: boolean
}): Promise<ApiFormattedResponse<AdminCampaignLinkToggle>> {
  const response = await mutate<{
    setCampaignLinksDisabled?: { data: AdminCampaignLinkToggle }
  }>({
    mutation: setCampaignLinksDisabledDoc,
    variables: { input }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.setCampaignLinksDisabled?.data
  }
}

export async function setCustomerLinksDisabled(input: {
  customerId: string
  disabled: boolean
  reason?: string
  pause?: boolean
}): Promise<ApiFormattedResponse<AdminCampaignLinks>> {
  const response = await mutate<{
    setCustomerLinksDisabled?: { data: AdminCampaignLinks }
  }>({
    mutation: setCustomerLinksDisabledDoc,
    variables: { input }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.setCustomerLinksDisabled?.data
  }
}
